/**
 * @description 指标列构建器
 */
import React from 'react';
import { getIntl } from 'umi';
import { formatKMBWithPlaceholder } from '@intelligence/v3/utils/number';
import { generateMetricKey, generateMetricRatioKey } from '@intelligence/v3/utils/metric';
import { CommonColumnBuilders } from '@intelligence/v3/utils/columns';
import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import { DataSourceBubbleInfo, DATASOURCE_NAME } from '@intelligence/components/DataSourceComponents';
import { METRICS_MAP } from '../../../constants';

import type { TableColumn } from 'tea-component';
import type { TDataSourceType, TMetricType, TTableSegment } from '../../../types';

/**
 * 指标列配置
 */
interface IMetricColumnConfig {
  /** 列key */
  key: TMetricType;
  /** 列标题 i18n key */
  headerKey: string;
  /** 列宽度 */
  width?: number;
  /** 取值字段 */
  field: keyof ICasualGenreProfileMetricData;
  /** 环比取值字段 */
  ratioField: keyof ICasualGenreProfileMetricData;
}

/**
 * 指标列配置映射
 */
const METRIC_COLUMN_CONFIGS: Record<TMetricType, IMetricColumnConfig> = {
  download: {
    key: 'download',
    headerKey: 'dbi_casual_table_col_download',
    width: 200,
    field: 'sensortower__download',
    ratioField: 'sensortower__download_ratio',
  },
  dau: {
    key: 'dau',
    headerKey: 'dbi_metric_dau',
    width: 200,
    field: 'sensortower__dau',
    ratioField: 'sensortower__dau_ratio',
  },
  wau: {
    key: 'wau',
    headerKey: 'dbi_metric_wau',
    width: 200,
    field: 'sensortower__wau',
    ratioField: 'sensortower__wau_ratio',
  },
  mau: {
    key: 'mau',
    headerKey: 'dbi_metric_mau',
    width: 200,
    field: 'sensortower__mau',
    ratioField: 'sensortower__mau_ratio',
  },
  revenue: {
    key: 'revenue',
    headerKey: 'dbi_casual_table_col_revenue',
    width: 200,
    field: 'sensortower__revenue',
    ratioField: 'sensortower__revenue_ratio',
  },
};

/**
 * 格式化环比值
 * @param ratioValue 环比值（小数形式，如 0.5 表示 50%）
 * @returns 格式化后的环比显示对象
 */
const formatRatioValue = (ratioValue: number | null | undefined): { type: string; value: string } => {
  if (ratioValue === null || ratioValue === undefined || Number.isNaN(ratioValue)) {
    return { type: 'none', value: '-' };
  }

  const percentage = Math.abs(ratioValue * 100).toFixed(1);

  if (ratioValue > 0) {
    return { type: 'up', value: `${percentage}%` };
  }
  if (ratioValue < 0) {
    return { type: 'down', value: `${percentage}%` };
  }
  return { type: 'none', value: '0%' };
};

/**
 * TopTip - 指标提示文本组件
 * 根据指标类型返回对应的说明文本
 */
const TopTip = (props: { title: string }) => {
  const { title } = props;
  const { formatMessage } = getIntl();
  let tip: React.ReactNode = '';

  switch (title) {
    case 'avg':
      tip = formatMessage({ id: 'dbi_info_avg_selected' });
      break;
    case 'sum':
      tip = formatMessage({ id: 'dbi_info_sum_selected' });
      break;
    case 'revenue':
      tip = (
        <>
          {formatMessage({ id: 'dbi_info_sum_selected' })}
          <br />
          {formatMessage({ id: 'dbi_info_revenue_removed' })}
        </>
      );
      break;
    default:
      tip = '';
      break;
  }

  return <>{tip}</>;
};

/**
 * 获取指标对应的提示类型
 * @param metric 指标类型
 * @returns 提示类型
 */
const getTipType = (metric: TMetricType): string => {
  switch (metric) {
    case 'dau':
    case 'wau':
    case 'mau':
      return 'avg';
    case 'download':
      return 'sum';
    case 'revenue':
      return 'revenue';
    default:
      return '';
  }
};

/**
 * 获取指标对应的 Metrics 页面 URL 参数值
 * @param metric 指标类型
 * @returns URL 中的 metric 参数值
 */
const getMetricUrlParam = (metric: TMetricType): string => {
  switch (metric) {
    case 'dau':
      return 'DAU';
    case 'mau':
      return 'MAU';
    case 'wau':
      return 'WAU';
    case 'download':
      return 'download';
    case 'revenue':
      return 'revenue';
    default:
      return metric;
  }
};

/**
 * 获取指标列标题的 hover 气泡内容
 * @param cfg 配置参数
 * @returns React节点
 */
const getTitleContent = (cfg: { dataSource: string; metric: string; textContent?: React.ReactNode }) => {
  const { dataSource, metric, textContent } = cfg || {};
  const { formatMessage } = getIntl();

  const element = textContent ? textContent : null;

  // 特定数据源和指标需要额外显示数据源范围提示
  const showDataSourceRangeInfo =
    ['appannie', 'sensortower'].includes(dataSource) &&
    ['dau', 'wau', 'mau', 'revenue', 'download', 'arpu'].includes(metric);

  return !showDataSourceRangeInfo ? (
    element
  ) : (
    <>
      {element}
      {element && (
        <>
          <br />
        </>
      )}
      {formatMessage({ id: 'dbi_info_mobile_datasource_range' })}
    </>
  );
};

/**
 * 构建带数据源 Icon 和环比的指标列
 * @param props 配置
 * @param props.locale 当前语言环境
 * @param props.dataSource 数据源
 * @param props.metric 指标类型
 * @returns 表格列配置
 */
export const buildMetricColumn = (props: {
  locale: string;
  dataSource: TDataSourceType;
  metric: TMetricType;
  /**
   * 排序状态列表
   */
  sorts: IRatioMetricSortState<ICasualGenreProfileMetricData>[];
  /**
   * 排序的回调
   * @param sorts 排序状态列表
   */
  onSortsChange?: (sorts: IRatioMetricSortState<ICasualGenreProfileMetricData>[]) => void;
}): TableColumn<ICasualGenreProfileMetricData> => {
  const { locale, dataSource, metric, sorts, onSortsChange } = props || {};
  const config = METRIC_COLUMN_CONFIGS[metric];
  const { key, headerKey, width = 200 } = config;
  const { formatMessage } = getIntl();
  const header = formatMessage({ id: headerKey });
  const metricKey = generateMetricKey({ dataSource, metric });
  const metricRatioKey = generateMetricRatioKey(metricKey);

  // 获取指标对应的提示类型并生成气泡内容
  const tipType = getTipType(metric);
  const tipsContent = getTitleContent({
    dataSource,
    metric,
    textContent: tipType ? <TopTip title={tipType} /> : undefined,
  });

  // 合并数据来源信息到气泡内容中
  const metricTips = (
    <>
      {tipsContent}
      <DataSourceBubbleInfo
        dataOfDom={header}
        metricKey={getMetricUrlParam(metric)}
        entityType="mobile"
        comesFormDom={
          <>
            <img style={{ marginRight: '4px', verticalAlign: 'middle' }} src={getDataSourceIcon(dataSource)} />
            {DATASOURCE_NAME[dataSource]}
          </>
        }
        dataSource={dataSource}
      />
    </>
  );

  return CommonColumnBuilders.buildKMBMetricColumn<ICasualGenreProfileMetricData>({
    locale,
    key,
    align: 'center',
    width,
    showRatio: true,
    dataSource,
    metric,
    metricName: header,
    getValue: (cvm) => {
      // @ts-ignore
      return cvm?.[metricKey] ?? null;
    },
    formatter: (value) => {
      return formatKMBWithPlaceholder(value);
    },
    getRatioValue: (cvm) => {
      // @ts-ignore
      return cvm?.[metricRatioKey] ?? null;
    },
    ratioValueFormatter: (ratioValue: any) => {
      return formatRatioValue(ratioValue);
    },
    sorts,
    onSortsChange,
    metricTips,
  });
};

/**
 * 根据指标和表格Segment获取指标列
 * @param props 参数
 * @param props.locale 当前语言环境
 * @param props.granularity 当前日期粒度
 * @param props.dataSource 当前数据源
 * @param props.metric 当前指标类型
 * @param props.tableSegment 表格展示列Segment值
 * @returns 指标列配置数组（仅在 tableSegment 为 metrics 时返回全部指标）
 */
export const getMetricColumns = (props: {
  locale: string;
  /**
   * 当前日期粒度
   */
  granularity?: IGranularity;
  dataSource: TDataSourceType;
  metric: TMetricType;
  tableSegment: TTableSegment;
  /**
   * 排序状态列表
   */
  sorts: IRatioMetricSortState<ICasualGenreProfileMetricData>[];
  /**
   * 排序的回调
   * @param sorts 排序状态列表
   */
  onSortsChange?: (sorts: IRatioMetricSortState<ICasualGenreProfileMetricData>[]) => void;
}): TableColumn<ICasualGenreProfileMetricData>[] => {
  const { granularity = 'monthly', tableSegment, ...restProps } = props;

  if (tableSegment !== 'metrics') {
    return [];
  }

  // 根据粒度返回对应的指标列
  const metrics: TMetricType[] = METRICS_MAP[granularity] || [];

  return metrics.map((metricItem) => {
    return buildMetricColumn({
      ...restProps,
      metric: metricItem,
    });
  });
};

export default getMetricColumns;
